// Width processor module
// Converts {{width: value}} ... {{/width}} to a centered wrapper with the given width

import { processTooltipReferences } from './footnotes.js';

/**
 * @param {Element}  container
 * @param {Map}      footnotesMap
 * @param {Function} recurse  - processAllSnippets(container, footnotesMap), passed to avoid circular import
 */
export function processWidthBlocks(container, footnotesMap, recurse) {
  const allElements = Array.from(container.children);
  const startRegex  = /\{\{width:\s*([^}]+)\}\}/;
  const endRegex    = /\{\{\/width\}\}/;

  const blocks = [];

  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const startMatch = (element.textContent || '').match(startRegex);
    if (!startMatch) continue;

    const widthValue = startMatch[1].trim();
    let endIndex = -1, endElement = null;

    for (let j = i; j < allElements.length; j++) {
      if ((allElements[j].textContent || '').match(endRegex)) {
        endIndex = j; endElement = allElements[j]; break;
      }
    }

    if (endIndex === -1) continue; // silently skip unclosed (same as original)

    blocks.push({ startIndex: i, endIndex, startElement: element, endElement, widthValue });
    i = endIndex;
  }

  // Process in reverse order to keep indices stable
  blocks.reverse().forEach(block => {
    if (!block.startElement?.parentNode || !block.endElement?.parentNode) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'width-wrapper';
    wrapper.style.width = block.widthValue;
    wrapper.style.marginLeft = 'auto';
    wrapper.style.marginRight = 'auto';

    // ── Collect content ────────────────────────────────────────────────────────
    const contentElements = [];

    const startHTML = block.startElement.innerHTML || '';
    const startHTMLMatch = startHTML.match(/\{\{width:\s*[^}]+\}\}\s*(.+)/s);
    if (startHTMLMatch?.[1]?.trim()) {
      const tmp = document.createElement('div');
      tmp.innerHTML = startHTMLMatch[1].trim();
      while (tmp.firstChild) contentElements.push(tmp.firstChild);
    }

    const currentChildren = Array.from(container.children);
    for (let k = block.startIndex + 1; k < block.endIndex && k < currentChildren.length; k++) {
      const mid = currentChildren[k];
      if (mid && mid.parentNode && mid !== block.startElement && mid !== block.endElement) {
        contentElements.push(mid.cloneNode(true));
      }
    }

    const endHTML = block.endElement.innerHTML || '';
    const endHTMLMatch = endHTML.match(/(.+?)\s*\{\{\/width\}\}/s);
    if (endHTMLMatch?.[1]?.trim()) {
      const tmp = document.createElement('div');
      tmp.innerHTML = endHTMLMatch[1].trim();
      const frag = document.createDocumentFragment();
      while (tmp.firstChild) frag.appendChild(tmp.firstChild);
      contentElements.push(...Array.from(frag.childNodes));
    }

    contentElements.forEach(el => wrapper.appendChild(el));
    block.startElement.replaceWith(wrapper);

    // Recurse into the wrapper
    if (recurse) recurse(wrapper, footnotesMap);
    else if (footnotesMap) processTooltipReferences(wrapper, footnotesMap);

    // Remove middle + end elements
    const afterReplace = Array.from(container.children);
    const newStart = afterReplace.indexOf(wrapper);
    if (newStart !== -1) {
      let endAfter = -1;
      for (let k = newStart + 1; k < afterReplace.length; k++) {
        if (afterReplace[k] === block.endElement) { endAfter = k; break; }
      }
      if (endAfter !== -1) {
        for (let k = endAfter; k > newStart; k--) {
          afterReplace[k]?.parentNode?.removeChild(afterReplace[k]);
        }
      } else {
        block.endElement?.parentNode?.removeChild(block.endElement);
      }
    }
  });
}
